import { useEffect, useRef, useState } from "react";
import { Pin, Pencil, Trash2 } from "lucide-react";
import { Avatar } from "@/components/shared/UI";
import { DeleteButton } from "@/components/shared/DeleteButton";

function NoticeDetails({ text, clamp, id }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (el && clamp && !open) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, clamp, open]);
  return (
    <>
      <p ref={ref} className={`mt-1 whitespace-pre-wrap text-sm text-slate-600 ${clamp && !open ? "line-clamp-3" : ""}`} data-testid={`notice-details-${id}`}>{text}</p>
      {clamp && (overflows || open) && (
        <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 text-xs font-semibold text-sky-600 hover:underline" data-testid={`notice-toggle-${id}`}>
          {open ? "Show less" : "View more"}
        </button>
      )}
    </>
  );
}

export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export function NoticeCard({ n, onEdit, onPin, onDeleted, compact }) {
  return (
    <div data-testid={`notice-${n.id}`} className={`rounded-xl border p-4 transition-colors ${n.pinned ? "border-amber-200 bg-amber-50/60" : "border-slate-200/80 bg-white"} ${compact ? "p-3" : ""}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-900">
            {n.pinned && <Pin className="h-3.5 w-3.5 shrink-0 fill-amber-500 text-amber-500" data-testid={`notice-pinned-${n.id}`} />}{n.title}
          </p>
          {n.details && <NoticeDetails text={n.details} clamp={compact} id={n.id} />}
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
            <Avatar name={n.created_by_name || "?"} className="h-5 w-5 text-[9px]" />
            <span data-testid={`notice-meta-${n.id}`}>{n.created_by_name || "Unknown"} · {fmtDateTime(n.created_at)}</span>
            {n.updated_at && <span>· edited</span>}
          </div>
        </div>
        {onEdit && (
          <div className="flex shrink-0 items-center gap-0.5">
            <button onClick={() => onPin(n)} title={n.pinned ? "Unpin" : "Pin to top"} data-testid={`notice-pin-${n.id}`}
              className={`rounded-md p-1.5 hover:bg-amber-100 ${n.pinned ? "text-amber-600" : "text-slate-400"}`}><Pin className="h-4 w-4" /></button>
            <button onClick={() => onEdit(n)} title="Edit" data-testid={`notice-edit-${n.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-sky-50 hover:text-sky-600"><Pencil className="h-4 w-4" /></button>
            <DeleteButton endpoint="/notices" id={n.id} label="notice" onDeleted={onDeleted}
              trigger={<button title="Delete" data-testid={`notice-delete-${n.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>} />
          </div>
        )}
      </div>
    </div>
  );
}
